import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Maximize, Minimize, Heart } from 'lucide-react';
import { getGameById, getSimilarGames } from '../data/gamesRegistry';
import gamesFeed from '../data/gamesFeed.json';
import { useHubStore } from '../store/useHubStore';

interface GamePageProps {
  gameId?: string;
  children?: React.ReactNode;
}

export const GamePage: React.FC<GamePageProps> = ({ gameId: propGameId, children }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const activeId = propGameId || params.id;
  const { wishlist, toggleWishlist } = useHubStore();
  
  let game = getGameById(activeId || '');
  let isFeed = false;
  
  if (!game && activeId) {
    game = (gamesFeed as any[]).find(g => g.id === activeId);
    
    if (!game && location.state?.game) {
      game = location.state.game;
    }
    
    if (!game && activeId.endsWith('-gm')) {
      const parts = activeId.split('-');
      if (parts.length >= 3) {
        const gmId = parts[parts.length - 2];
        const titleSlug = parts.slice(0, parts.length - 2).join(' ');
        game = {
          id: activeId,
          title: titleSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          category: 'Game',
          thumbnail: `https://img.gamemonetize.com/${gmId}/512x384.jpg`,
          url: `https://html5.gamemonetize.com/${gmId}/`,
          isFeedGame: true
        } as any;
      }
    }
    
    isFeed = !!game;
  }
  
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navigate = useNavigate();

  if (!game) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Game not found!</div>;
  }

  const isWishlisted = activeId ? wishlist.includes(activeId) : false;

  // If it's a feed game, we can just pick random similar games from the feed, 
  // or just use local similar games. For simplicity, we just use local similar games.
  const similarGames = getSimilarGames(game.category, game.id, 6);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Error attempting to enable fullscreen:', err);
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Listen for fullscreen change to update state if user presses ESC
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <Helmet>
        <title>Play {game.title} Free Online | Gamezolve</title>
        <meta name="description" content={`Play ${game.title} online for free. ${game.description?.substring(0, 100)}...`} />
        <meta name="keywords" content={`${game.title}, play ${game.title}, free online games, HTML5 games, ${game.category} games`} />
        <meta property="og:title" content={`Play ${game.title} Free Online | Gamezolve`} />
        <meta property="og:description" content={`Play ${game.title} online for free. ${game.description?.substring(0, 100)}...`} />
        <meta property="og:image" content={game.thumbnail} />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* GAME CONTAINER SECTION */}
      <div style={{ display: 'flex', gap: '20px', flexDirection: 'column', alignItems: 'center' }}>
        
        {/* The actual game wrapper */}
        <div 
          ref={containerRef}
          style={{ 
            width: '100%', 
            maxWidth: '800px', 
            height: isFullscreen ? '100vh' : '80vh', 
            maxHeight: isFullscreen ? 'none' : '800px',
            backgroundColor: '#000',
            borderRadius: isFullscreen ? '0' : '15px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: isFullscreen ? 'none' : '0 10px 30px rgba(0,0,0,0.2)'
          }}
        >
          {isFeed ? (
            <iframe 
              src={(game as any).url}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={game.title}
              allowFullScreen
            />
          ) : (
            children
          )}
          
          {/* Maximize Button overlay */}
          <button 
            onClick={toggleFullscreen}
            style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              background: 'rgba(0,0,0,0.5)',
              border: '2px solid rgba(255,255,255,0.5)',
              color: 'white',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              zIndex: 9999, // High z-index to stay above game UI
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={isFullscreen ? "Exit Fullscreen" : "Maximize Game"}
          >
            {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          </button>
        </div>
        
        <div style={{ maxWidth: '800px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h1 style={{ margin: 0, fontSize: '2rem', fontFamily: 'var(--font-family-game)', color: '#111827' }}>
              {game.title}
            </h1>
            <button 
              onClick={() => activeId && toggleWishlist(activeId)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isWishlisted ? '#ef4444' : '#9ca3af',
                transition: 'color 0.2s ease, transform 0.2s ease',
                transform: isWishlisted ? 'scale(1.1)' : 'scale(1)',
                padding: '5px'
              }}
              title={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
            >
              <Heart size={28} fill={isWishlisted ? '#ef4444' : 'none'} strokeWidth={2.5} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Link to={`/wiki/${game.id}`} style={{ background: '#10b981', color: 'white', padding: '6px 16px', borderRadius: '20px', textDecoration: 'none', fontWeight: 'bold', fontSize: '0.9rem', boxShadow: '0 2px 5px rgba(16, 185, 129, 0.3)' }}>
              📖 Read Wiki & Guide
            </Link>
            <span style={{ background: '#e5e7eb', padding: '6px 16px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.9rem', color: '#4b5563' }}>
              {game.category}
            </span>
          </div>
        </div>
      </div>

      <hr style={{ margin: '40px 0', border: 'none', borderTop: '2px solid #e5e7eb' }} />

      {/* SIMILAR GAMES SECTION */}
      <div>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '20px', color: '#111827' }}>Similar Games</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px' }}>
          {similarGames.map(sg => (
            <Link 
              key={sg.id} 
              to={sg.path}
              style={{ textDecoration: 'none' }}
            >
              <div style={{
                position: 'relative',
                aspectRatio: '1/1',
                backgroundColor: '#e5e7eb',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <img src={sg.thumbnail} alt={sg.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => {
                  // Fallback if thumbnail missing
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.style.background = 'linear-gradient(135deg, #3b82f6, #8b5cf6)';
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: 0, left: 0, right: 0,
                  padding: '30px 10px 10px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  {sg.title}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <hr style={{ margin: '40px 0', border: 'none', borderTop: '2px solid #e5e7eb' }} />

      {/* ABOUT GAME SECTION */}
      <div style={{ maxWidth: '800px', width: '100%', marginTop: '20px' }}>
        
        {/* STATS STRIP */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '15px', 
          background: '#f9fafb', 
          padding: '20px', 
          borderRadius: '15px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 'bold' }}>Rating</div>
            <div style={{ fontSize: '1.2rem', color: '#111827', fontWeight: 'bold' }}>{game.rating || 'New!'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 'bold' }}>Gameplays</div>
            <div style={{ fontSize: '1.2rem', color: '#111827', fontWeight: 'bold' }}>{game.gameplays || '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 'bold' }}>Released</div>
            <div style={{ fontSize: '1.2rem', color: '#111827', fontWeight: 'bold' }}>{game.released || '2024'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 'bold' }}>Technology</div>
            <div style={{ fontSize: '1.2rem', color: '#111827', fontWeight: 'bold' }}>{game.technology || 'HTML5'}</div>
          </div>
        </div>

        {/* TAGS STRIP */}
        {game.richTags && game.richTags.length > 0 ? (
          <div style={{ marginBottom: '30px' }}>
            <div style={{ fontSize: '1rem', color: '#6b7280', fontWeight: 'bold', marginBottom: '10px' }}>Tagged in</div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {game.richTags.map((tag: any, idx: number) => (
                <a key={idx} href={tag.url} style={{
                  textDecoration: 'none',
                  background: 'var(--accent, #3b82f6)',
                  color: 'white',
                  padding: '8px 15px',
                  borderRadius: '20px',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {tag.emoji} {tag.text}
                </a>
              ))}
            </div>
          </div>
        ) : game.tags && game.tags.length > 0 ? (
          <div style={{ marginBottom: '30px' }}>
            <div style={{ fontSize: '1rem', color: '#6b7280', fontWeight: 'bold', marginBottom: '10px' }}>Tags</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {(game.tags as string[]).map((tag, idx) => (
                <span key={idx} style={{
                  background: '#e5e7eb',
                  color: '#4b5563',
                  padding: '5px 12px',
                  borderRadius: '15px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold'
                }}>
                  #{tag.trim()}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {/* DETAILED HTML DESCRIPTION & INSTRUCTIONS */}
        {game.descriptionHtml ? (
          <div 
            className="game-detailed-info"
            style={{ lineHeight: '1.8', color: '#374151', fontSize: '1.1rem' }}
            dangerouslySetInnerHTML={{ __html: game.descriptionHtml }}
          />
        ) : (
          <div className="game-detailed-info" style={{ lineHeight: '1.8', color: '#374151', fontSize: '1.1rem' }}>
            <p>{game.description}</p>
            
            {game.instructions ? (
              <>
                <h3>How to Play {game.title}</h3>
                <div style={{ background: '#f3f4f6', padding: '20px', borderRadius: '12px', borderLeft: '4px solid #3b82f6' }}>
                  <p style={{ margin: 0, fontWeight: '500' }}>{game.instructions}</p>
                </div>
              </>
            ) : (
              <>
                <h3>How to Play {game.title}?</h3>
                <p>Dive in and explore the game mechanics directly! Tap or click to interact with the game elements and discover the objective.</p>
              </>
            )}
          </div>
        )}
        
        <style>{`
          .game-detailed-info h3 {
            font-size: 1.5rem;
            color: #111827;
            margin-top: 40px;
            margin-bottom: 15px;
            font-family: var(--font-family-game, inherit);
          }
          .game-detailed-info ul {
            padding-left: 20px;
            margin-bottom: 20px;
          }
          .game-detailed-info li {
            margin-bottom: 10px;
          }
          .game-detailed-info p {
            margin-bottom: 20px;
          }
          .game-detailed-info a {
            color: #3b82f6;
            text-decoration: none;
            font-weight: bold;
          }
          .game-detailed-info a:hover {
            text-decoration: underline;
          }
        `}</style>
      </div>
      
    </div>
  );
};
